import { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { submit } from 'redux-form';
import { getPacks } from '../../actions/packs';
import ModalDialog from '../../components/ModalDialog';
import OrderableDataGrid from '../../components/OrderableDataGrid';

// The items of one kind in one pack (or without pack): paginated list, order, edit dialog, delete.
const PackItemsList = ({ packId, kind, items, itemsCount, selected, packs }) => {
  const dispatch = useDispatch();
  const [limit, setLimit] = useState(10);
  const [skip, setSkip] = useState(0);
  const [filter, setFilter] = useState('');
  const [openDialog, setOpenDialog] = useState(false);

  // The list state is shared by every pack: empty it on mount, so another pack's items never show here.
  useEffect(() => {
    dispatch(kind.reset());
  }, [dispatch, kind]);

  // `packs` is a dependency so that getPacks() after a create/update/delete refetches the list
  useEffect(() => {
    dispatch(kind.getPerPage(limit, skip, filter, packId));
  }, [dispatch, kind, limit, skip, filter, packId, packs]);

  const formatData = useCallback(
    () => ({
      data: [...items]
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
        .map((item) => ({
          objectId: item.objectId,
          image: `https://storage.googleapis.com/silhouette-users/${kind.imageFolder}/${item.image}`,
          name: item.name ?? item.title,
          description: item.description,
        })),
      totalCount: itemsCount ?? items.length,
    }),
    [items, itemsCount, kind.imageFolder]
  );

  const handleOptionChange = useCallback(
    (e) => {
      if (e.fullName === 'searchPanel.text') setFilter(e.value);
      if (e.fullName === 'paging.pageSize') setLimit(e.value);
      if (e.fullName === 'paging.pageIndex') setSkip(e.value * limit);
    },
    [limit]
  );

  const handleMove = (id, up = false) => {
    const sorted = [...items].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    const index = sorted.findIndex((item) => item.objectId === id);
    const target = up ? index - 1 : index + 1;
    if (target < 0 || target >= sorted.length) return;
    [sorted[index], sorted[target]] = [sorted[target], sorted[index]];
    dispatch(kind.reorder(sorted));
  };

  const handleEdit = (id) => {
    dispatch(kind.edit(id));
    setOpenDialog(true);
  };

  const handleUpdate = async (values) => {
    kind.validate(values);
    await dispatch(kind.update(selected, values));
    setOpenDialog(false);
    dispatch(getPacks()); // counts; also refetches the list (the item may have left the pack)
  };

  const { Form } = kind;
  return (
    <>
      <OrderableDataGrid
        onFormatData={formatData}
        onEdit={handleEdit}
        onDelete={async (id) => {
          await dispatch(kind.delete(id));
          dispatch(getPacks());
        }}
        onChange={handleOptionChange}
        onMove={handleMove}
        FilterText={filter}
      />
      <ModalDialog
        title={`Modifier ${selected?.name ?? selected?.title ?? ''}`}
        content={<Form onSubmit={handleUpdate} />}
        isVisible={openDialog}
        onConfirm={() => dispatch(submit(kind.formName))}
        onClose={() => setOpenDialog(false)}
        labelConfirm="Enregistrer"
      />
    </>
  );
};

PackItemsList.propTypes = {
  packId: PropTypes.string.isRequired,
  kind: PropTypes.object.isRequired,
  items: PropTypes.array.isRequired,
  itemsCount: PropTypes.number,
  selected: PropTypes.object,
  packs: PropTypes.array,
};

export default PackItemsList;
